import type { Option } from '@/constants/institute';
// One definition of "what does this half-typed number field mean", shared with
// the class setup screen rather than written twice (CLAUDE.md §3).
import { toAmount } from '@/features/institute/classes/utils';
import { instituteToday } from '@/features/teacher/utils';
import type { InstituteClass } from '@/types/instituteClass';
import type { AdmissionFormValues } from './schemas/admission.schema';

/** Completed years and months, the way an institute states a child's age. */
export interface Age {
  years: number;
  months: number;
}

/**
 * How old a child born on `dateOfBirth` is on `on`.
 *
 * Months matter: whether a child is admitted to Nursery or LKG turns on a few
 * months, so "8 years" alone would not answer the question being asked.
 * Returns `null` for a blank, unparseable or future birth date — the caller
 * then shows nothing rather than a nonsense age.
 */
export function ageOn(dateOfBirth: string, on: string | Date): Age | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) return null;

  const born = new Date(dateOfBirth);
  const reference = new Date(on);
  if (Number.isNaN(born.getTime()) || Number.isNaN(reference.getTime())) return null;

  reference.setHours(0, 0, 0, 0);
  if (born > reference) return null;

  let years = reference.getFullYear() - born.getFullYear();
  let months = reference.getMonth() - born.getMonth();
  if (reference.getDate() < born.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years, months };
}

/** `{ years: 8, months: 4 }` → `8 years, 4 months`. */
export function formatAge(age: Age): string {
  const yearPart = `${age.years} ${age.years === 1 ? 'year' : 'years'}`;
  const monthPart = `${age.months} ${age.months === 1 ? 'month' : 'months'}`;
  return `${yearPart}, ${monthPart}`;
}

/**
 * `{ years: 8, months: 4 }` → `8y 4m`, for a table cell.
 *
 * Beside `formatAge` rather than inside the register that wanted it: an age is
 * written the same way wherever it appears, and the long and short forms have
 * to agree about what they are shortening (CLAUDE.md §3).
 */
export function formatAgeShort(age: Age): string {
  return `${age.years}y ${age.months}m`;
}

/** `2017-03-14` → `8 years, 4 months`, as of today. */
export function describeAge(dateOfBirth: string): string | null {
  const age = ageOn(dateOfBirth, new Date());
  return age ? formatAge(age) : null;
}

/**
 * A class the institute has actually set an admission age rule for. Both ends are
 * stored together, so either both are set or the class has no rule at all.
 */
type ClassWithAgeRule = InstituteClass & { minAgeYears: number; maxAgeYears: number };

function hasAgeRule(instituteClass: InstituteClass): instituteClass is ClassWithAgeRule {
  return instituteClass.minAgeYears !== null && instituteClass.maxAgeYears !== null;
}

function admitsAge(instituteClass: ClassWithAgeRule, years: number): boolean {
  return years >= instituteClass.minAgeYears && years <= instituteClass.maxAgeYears;
}

/** `13–14 years`, or `null` for a class the institute set no rule for. */
export function describeAgeRange(instituteClass: InstituteClass): string | null {
  if (!hasAgeRule(instituteClass)) return null;
  const { minAgeYears, maxAgeYears } = instituteClass;
  return minAgeYears === maxAgeYears
    ? `${minAgeYears} years`
    : `${minAgeYears}–${maxAgeYears} years`;
}

/**
 * The class a child of this age should be placed in, by the institute's own
 * criteria — or `undefined` when no class covers that age.
 *
 * Age bands touch at their edges: on the usual chart a 14-year-old satisfies
 * both Class 8 (13–14) and Class 9 (14–15). The higher class is the one meant,
 * because its minimum is the rule the child has only just met — so the highest
 * minimum wins rather than the first match in teaching order.
 *
 * Classes with no age rule are skipped entirely. An institute that has not set its
 * criteria gets no suggestion, which is better than a wrong one.
 */
export function classForAge(classes: InstituteClass[], years: number): InstituteClass | undefined {
  let best: ClassWithAgeRule | undefined;

  for (const instituteClass of classes) {
    if (!hasAgeRule(instituteClass) || !admitsAge(instituteClass, years)) continue;
    if (!best || instituteClass.minAgeYears > best.minAgeYears) best = instituteClass;
  }

  return best;
}

/**
 * Why the chosen class does not suit this child's age, or `null` when it does.
 *
 * A warning, never a block: repeaters, late admissions and children moving in
 * mid-year are all ordinary, and an office that cannot record one would simply
 * put in a false date of birth instead.
 */
export function ageMismatch(
  instituteClass: InstituteClass | undefined,
  age: Age | null,
): string | null {
  if (!instituteClass || !age || !hasAgeRule(instituteClass)) return null;
  if (admitsAge(instituteClass, age.years)) return null;

  return `This child is ${formatAge(age)} old on the date of admission, and ${instituteClass.name} takes ${describeAgeRange(instituteClass)}. You can still save this if it is right.`;
}

export interface FeeLedger {
  tuitionNet: number;
  tuitionBalance: number;
  booksNet: number;
  booksBalance: number;
  otherNet: number;
  otherBalance: number;
  totalFee: number;
  totalDiscount: number;
  totalPaid: number;
  totalBalance: number;
}

/**
 * The running total under the fee fields.
 *
 * Whole rupees throughout: the inputs accept nothing else, so every figure here
 * is integer arithmetic and none of it can drift the way a float would. The
 * server recalculates all of it before anything is written — this only tells
 * the person typing what they have just done.
 */
export function feeLedger(values: {
  tuitionFee: string;
  tuitionDiscount: string;
  tuitionPaid: string;
  booksFee: string;
  booksDiscount: string;
  booksPaid: string;
  otherFee: string;
  otherDiscount: string;
  otherPaid: string;
}): FeeLedger {
  const tuitionFee = toAmount(values.tuitionFee);
  const tuitionDiscount = toAmount(values.tuitionDiscount);
  const booksFee = toAmount(values.booksFee);
  const booksDiscount = toAmount(values.booksDiscount);
  const tuitionPaid = toAmount(values.tuitionPaid);
  const booksPaid = toAmount(values.booksPaid);
  const otherFee = toAmount(values.otherFee);
  const otherDiscount = toAmount(values.otherDiscount);
  const otherPaid = toAmount(values.otherPaid);

  // Clamped at zero so a half-typed discount cannot show a negative payable.
  const tuitionNet = Math.max(0, tuitionFee - tuitionDiscount);
  const booksNet = Math.max(0, booksFee - booksDiscount);
  const otherNet = Math.max(0, otherFee - otherDiscount);

  return {
    tuitionNet,
    tuitionBalance: Math.max(0, tuitionNet - tuitionPaid),
    booksNet,
    booksBalance: Math.max(0, booksNet - booksPaid),
    otherNet,
    otherBalance: Math.max(0, otherNet - otherPaid),
    totalFee: tuitionFee + booksFee + otherFee,
    totalDiscount: tuitionDiscount + booksDiscount + otherDiscount,
    totalPaid: tuitionPaid + booksPaid + otherPaid,
    totalBalance: Math.max(
      0,
      tuitionNet + booksNet + otherNet - tuitionPaid - booksPaid - otherPaid,
    ),
  };
}

/**
 * `"36000.00"` → `"36000"`. Fees are `DECIMAL` columns and come back with
 * paise; the form takes whole rupees, and a field showing "36000.00" invites
 * someone to edit it into something the server rejects.
 */
export function toWholeRupees(amount: string): string {
  const value = Number(amount);
  return Number.isFinite(value) ? String(Math.round(value)) : '0';
}

/**
 * Classes as the placement dropdown offers them, in the institute's own order,
 * each labelled with the ages it takes — so the office can see why a class was
 * suggested, and what it is choosing instead if it overrides one.
 */
export function classOptions(classes: InstituteClass[]): Option[] {
  return classes.map((instituteClass) => {
    const range = describeAgeRange(instituteClass);
    return {
      value: instituteClass.uuid,
      label: range ? `${instituteClass.name} · ${range}` : instituteClass.name,
    };
  });
}

/**
 * The sections of one class, each labelled with its seats — the office needs to
 * know that 8-A is full before it puts a child there, not afterwards.
 */
export function sectionOptions(instituteClass: InstituteClass | undefined): Option[] {
  if (!instituteClass) return [];
  return instituteClass.sections.map((section) => ({
    value: section.uuid,
    label: `${section.name} · ${section.maxSeats} seats`,
  }));
}

export const EMPTY_ADMISSION_FORM: AdmissionFormValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  // Gender and category start empty so the office chooses each one; a
  // default here was how a child the office forgot to correct got admitted as
  // a boy in the general category.
  gender: '',
  dateOfBirth: '',
  bloodGroup: 'UNKNOWN',
  religion: '',
  category: '',
  // Empty, like the two above: it is a required field the office fills in,
  // not a value the form assumes for every child.
  nationality: '',
  aadhaarNumber: '',

  classUuid: '',
  sectionUuid: '',
  // The school's day, not the device's: a laptop set to another zone would
  // otherwise date an evening admission tomorrow or yesterday.
  admissionDate: instituteToday(),
  admissionType: 'REGULAR',
  previousSchool: '',
  previousClass: '',
  transferCertificateNumber: '',

  fatherName: '',
  fatherOccupation: '',
  fatherMobile: '',
  fatherEmail: '',
  motherName: '',
  motherOccupation: '',
  motherMobile: '',
  motherEmail: '',
  guardianName: '',
  guardianRelationship: '',
  guardianMobile: '',
  primaryContact: 'FATHER',

  addressLine1: '',
  addressLine2: '',
  city: '',
  district: '',
  state: '',
  pincode: '',

  usesTransport: false,
  routeName: '',
  busNumber: '',
  pickupPoint: '',

  tuitionFee: '0',
  tuitionDiscount: '0',
  tuitionPaid: '0',
  booksFee: '0',
  booksDiscount: '0',
  booksPaid: '0',
  otherFee: '0',
  otherDiscount: '0',
  otherPaid: '0',
  concessionReason: '',
  feeDueDate: '',
};
